import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { ISSUE_TYPES } from '../questions/ChartAnnotation';
import EditorHelpPanel from './EditorHelpPanel';
import useGuidedTutorial from './useGuidedTutorial';
import GuidedTutorialOverlay from './GuidedTutorialOverlay';
import FacilitatorPageHeader from './FacilitatorPageHeader';
import { generateExercisePatient } from '../../lib/randomPatient';

// Authoring shell for spot-the-error drills. Mirrors the FacilitatorDrillEditor
// pattern: title + summary + flags at the top, then a structured payload editor
// (patient details, allergies, context, prescription rows, expected issues).

const DIFFICULTIES = [
  { value: 'starter', label: 'Starter' },
  { value: 'standard', label: 'Standard' },
  { value: 'advanced', label: 'Advanced' },
];

const blankRx = () => ({ drug: '', dose: '', frequency: '', route: '', indication: '' });
const blankAllergy = () => ({ drug: '', reaction: '' });
const blankIssue = () => ({ drug: '', issueType: 'wrong_dose', explanation: '' });

const buildInitialState = (drill) => {
  const payload = drill?.payload || {};
  const scenario = payload.scenario || {};
  return {
    title: drill?.title || '',
    summary: drill?.summary || '',
    freeTier: Boolean(drill?.freeTier),
    difficulty: drill?.difficulty || 'standard',
    timeLimitSeconds: payload.timeLimitSeconds || 90,
    patient: {
      name: scenario.patient?.name || '',
      age: scenario.patient?.age || '',
      gender: scenario.patient?.gender || '',
      weight: scenario.patient?.weight || '',
      otherDetails: scenario.patient?.otherDetails || '',
    },
    allergies: Array.isArray(scenario.allergies) && scenario.allergies.length
      ? scenario.allergies.map((a) => ({ drug: a.drug || '', reaction: a.reaction || '' }))
      : [blankAllergy()],
    context: scenario.context || '',
    prescriptionList: Array.isArray(scenario.prescriptionList) && scenario.prescriptionList.length
      ? scenario.prescriptionList.map((rx) => ({
          drug: rx.drug || '',
          dose: rx.dose || '',
          frequency: rx.frequency || '',
          route: rx.route || '',
          indication: rx.indication || '',
        }))
      : [blankRx()],
    expectedIssues: Array.isArray(payload.expectedIssues) && payload.expectedIssues.length
      ? payload.expectedIssues.map((iss) => ({
          drug: iss.drug || '',
          issueType: iss.issueType || 'wrong_dose',
          explanation: iss.explanation || '',
        }))
      : [blankIssue()],
  };
};

const FacilitatorSpotErrorEditor = ({
  drill, // null = new drill, else { id, title, ... } from list (then loaded fully)
  loadFullDrill, // async (id) => { drill: {...} }; required when editing
  onSave, // async ({ title, summary, freeTier, difficulty, payload }) => void
  onPreview, // optional: ({ title, payload }) => void
  onCancel,
  saving = false,
  errorMessage,
}) => {
  const [state, setState] = useState(() => buildInitialState(drill));
  const [loading, setLoading] = useState(false);
  const isEdit = Boolean(drill?.id);

  // Refs for the guided walkthrough's highlight targets.
  const basicsRef = useRef(null);
  const patientRef = useRef(null);
  const chartRef = useRef(null);
  const issuesRef = useRef(null);
  const saveBarRef = useRef(null);

  const tutorialSteps = useMemo(() => ([
    { key: 'basics', title: 'Exercise basics', body: 'Start with a short title + one-line summary, pick a difficulty, and set a time limit (60-120 seconds is typical).', ref: basicsRef },
    { key: 'patient', title: 'Patient + scenario', body: 'Add the patient details + clinical context that orient the student. Include allergies + anything that drives prescribing decisions (CrCl, lithium level, weight).', ref: patientRef },
    { key: 'chart', title: 'Prescription chart', body: 'Add every prescription the student will see. Mix correct ones with the deliberate errors - the errors get described in the next section.', ref: chartRef },
    { key: 'issues', title: 'Expected issues (the answer key)', body: 'For each error: drug name (must match a chart row), pick the issue type, and write a one-sentence explanation that appears in post-submit feedback. 3-6 issues is a balanced exercise.', ref: issuesRef },
    { key: 'save', title: 'Save then publish', body: 'Save creates a draft. Use the publish button on the library page when you are ready to release to students.', ref: saveBarRef },
  ]), []);
  const tutorial = useGuidedTutorial(tutorialSteps);

  // If we were given a drill summary (from the list page) but no full payload,
  // fetch the full record so we can edit the scenario + expected issues.
  useEffect(() => {
    if (!isEdit) return;
    if (drill?.payload) return; // already full
    if (!loadFullDrill) return;
    let cancelled = false;
    setLoading(true);
    loadFullDrill(drill.id)
      .then((full) => {
        if (cancelled) return;
        setState(buildInitialState(full?.drill || full || {}));
      })
      .catch(() => { /* parent surfaces error */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isEdit, drill?.id, drill?.payload, loadFullDrill]);

  const updatePatient = (key, val) => setState((s) => ({ ...s, patient: { ...s.patient, [key]: val } }));
  const updateRx = (idx, key, val) => setState((s) => ({
    ...s,
    prescriptionList: s.prescriptionList.map((rx, i) => i === idx ? { ...rx, [key]: val } : rx),
  }));
  const updateAllergy = (idx, key, val) => setState((s) => ({
    ...s,
    allergies: s.allergies.map((a, i) => i === idx ? { ...a, [key]: val } : a),
  }));
  const updateIssue = (idx, key, val) => setState((s) => ({
    ...s,
    expectedIssues: s.expectedIssues.map((iss, i) => i === idx ? { ...iss, [key]: val } : iss),
  }));

  const addRx = () => setState((s) => ({ ...s, prescriptionList: [...s.prescriptionList, blankRx()] }));
  const removeRx = (idx) => setState((s) => ({
    ...s,
    prescriptionList: s.prescriptionList.length > 1 ? s.prescriptionList.filter((_, i) => i !== idx) : s.prescriptionList,
  }));
  const addAllergy = () => setState((s) => ({ ...s, allergies: [...s.allergies, blankAllergy()] }));
  const removeAllergy = (idx) => setState((s) => ({
    ...s,
    allergies: s.allergies.length > 1 ? s.allergies.filter((_, i) => i !== idx) : s.allergies,
  }));
  const addIssue = () => setState((s) => ({ ...s, expectedIssues: [...s.expectedIssues, blankIssue()] }));
  const removeIssue = (idx) => setState((s) => ({
    ...s,
    expectedIssues: s.expectedIssues.length > 1 ? s.expectedIssues.filter((_, i) => i !== idx) : s.expectedIssues,
  }));

  const validation = useMemo(() => {
    const errors = [];
    if (!state.title.trim()) errors.push('Title is required.');
    if (!state.summary.trim()) errors.push('Summary is required so students can pick a drill from the list.');
    const cleanRx = state.prescriptionList.filter((rx) => rx.drug.trim());
    if (cleanRx.length === 0) errors.push('At least one prescription is required.');
    const cleanIssues = state.expectedIssues.filter((iss) => iss.drug.trim());
    if (cleanIssues.length === 0) errors.push('At least one expected issue is required for grading.');
    cleanIssues.forEach((iss, i) => {
      const matchesAnyRx = cleanRx.some((rx) =>
        rx.drug.toLowerCase().trim() === iss.drug.toLowerCase().trim() ||
        rx.drug.toLowerCase().includes(iss.drug.toLowerCase().trim()) ||
        iss.drug.toLowerCase().includes(rx.drug.toLowerCase().trim())
      );
      if (!matchesAnyRx) {
        errors.push(`Expected issue ${i + 1} ("${iss.drug}") does not match any prescription on the chart.`);
      }
    });
    if (state.timeLimitSeconds < 15 || state.timeLimitSeconds > 600) {
      errors.push('Time limit must be between 15 and 600 seconds.');
    }
    return errors;
  }, [state]);

  // Same shape that handleSave produces — the server's snapshot
  // (scenario + expectedIssues + timeLimitSeconds) reads straight off
  // this payload, so reusing it for the preview means the preview is
  // faithful to what students will actually see.
  const buildPayload = () => ({
    scenario: {
      patient: { ...state.patient },
      allergies: state.allergies.filter((a) => a.drug.trim()),
      context: state.context,
      prescriptionList: state.prescriptionList.filter((rx) => rx.drug.trim()),
    },
    expectedIssues: state.expectedIssues
      .filter((iss) => iss.drug.trim())
      .map((iss) => ({
        drug: iss.drug.trim(),
        issueType: iss.issueType,
        explanation: iss.explanation,
      })),
    timeLimitSeconds: Number(state.timeLimitSeconds),
  });

  const handleSave = () => {
    if (validation.length > 0) return;
    onSave?.({
      title: state.title.trim(),
      summary: state.summary.trim(),
      freeTier: state.freeTier,
      difficulty: state.difficulty,
      payload: buildPayload(),
    });
  };

  const handlePreview = () => {
    if (!onPreview) return;
    onPreview({
      title: state.title.trim() || 'Untitled preview',
      payload: buildPayload(),
    });
  };

  if (loading) {
    return (
      <div className="student-page">
        <Container className="mt-4">
          <Spinner animation="border" /> Loading drill…
        </Container>
      </div>
    );
  }

  return (
    <div className="student-page">
      <Container className="mt-4 facilitator-editor">
        <div ref={saveBarRef} className={tutorial.isStepActive('save') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}>
          <FacilitatorPageHeader
            title={isEdit ? 'Edit spot-the-error exercise' : 'New spot-the-error exercise'}
            onBack={onCancel}
            backLabel="Cancel"
            actions={<>
              <Button type="button" variant="outline-secondary" size="sm" onClick={tutorial.start}>
                <i className="bi bi-question-circle me-1" /> Walkthrough
              </Button>
              {onPreview ? (
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={handlePreview}
                  disabled={validation.length > 0}
                  title={validation.length > 0 ? 'Fix the warnings above to preview' : 'Play through the exercise as a student would see it'}
                >
                  <i className="bi bi-play-circle me-1" /> Preview
                </Button>
              ) : null}
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={saving || validation.length > 0}
              >
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create draft'}
              </Button>
            </>}
          />
        </div>

        {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
        {validation.length > 0 ? (
          <Alert variant="warning">
            <strong>Before saving:</strong>
            <ul className="mb-0">{validation.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </Alert>
        ) : null}

        <EditorHelpPanel title="How to author a spot-the-error exercise" defaultOpen={!isEdit}>
          <p>A spot-the-error exercise shows the student a brief patient + a prescription chart and asks them to flag prescribing issues against a clock. The engine grades on <strong>precision</strong> (don't flag things that aren't issues) and <strong>recall</strong> (catch every real issue).</p>
          <ol>
            <li><strong>Exercise basics</strong> — short title and one-line summary (shown in the catalogue). Pick a difficulty (Starter / Standard / Advanced) and a time limit (15-600 seconds; 60-120s is typical).</li>
            <li><strong>Patient + scenario</strong> — name, age, gender, weight, anything clinically relevant (CrCl, lithium level, etc.). Add allergies. Use the clinical context for the brief that orients the student (admission day, PMH, what's just been started).</li>
            <li><strong>Prescription chart</strong> — add every prescription the student will see. Mix correct prescriptions with the deliberate errors. The errors live in the next section.</li>
            <li><strong>Expected issues</strong> (the answer key) — for each error: type the drug name (must match a chart row), pick the issue type from the dropdown, write a one-sentence explanation that will appear in the post-submit feedback.</li>
          </ol>
          <p><strong>Free tier</strong> — leave OFF unless the drill is curriculum-funded by Auxtechna and you want it visible to non-paying students on the public site.</p>
          <p><strong>Tip:</strong> a good drill has 3-6 issues across 5-8 prescriptions. Too few = trivial; too many = no time to think.</p>
        </EditorHelpPanel>

        <div ref={basicsRef} className={`card mb-3 ${tutorial.isStepActive('basics') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
          <div className="card-body">
            <h5>Exercise basics</h5>
            <Row className="g-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Title</Form.Label>
                  <Form.Control type="text" value={state.title} onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Difficulty</Form.Label>
                  <Form.Select value={state.difficulty} onChange={(e) => setState((s) => ({ ...s, difficulty: e.target.value }))}>
                    {DIFFICULTIES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Time limit (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    min={15}
                    max={600}
                    value={state.timeLimitSeconds}
                    onChange={(e) => setState((s) => ({ ...s, timeLimitSeconds: Number(e.target.value) || 90 }))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Summary (shown on the drill list)</Form.Label>
                  <Form.Control as="textarea" rows={2} value={state.summary} onChange={(e) => setState((s) => ({ ...s, summary: e.target.value }))} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Check
                  type="switch"
                  id="spot-error-freetier"
                  label="Free-tier eligible (visible to free students on the public tenant)"
                  checked={state.freeTier}
                  onChange={(e) => setState((s) => ({ ...s, freeTier: e.target.checked }))}
                />
              </Col>
            </Row>
          </div>
        </div>

        <div ref={patientRef} className={`card mb-3 ${tutorial.isStepActive('patient') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <h5 className="mb-0">Patient + scenario</h5>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setState((s) => ({ ...s, patient: generateExercisePatient() }))}
                title="Roll random demographics using the same name + address pool the EPMA simulator uses"
              >
                <i className="bi bi-shuffle me-1" /> Randomise patient
              </Button>
            </div>
            <Row className="g-2">
              <Col md={4}><Form.Group><Form.Label>Name</Form.Label><Form.Control value={state.patient.name} onChange={(e) => updatePatient('name', e.target.value)} /></Form.Group></Col>
              <Col md={2}><Form.Group><Form.Label>Age</Form.Label><Form.Control value={state.patient.age} onChange={(e) => updatePatient('age', e.target.value)} /></Form.Group></Col>
              <Col md={2}><Form.Group><Form.Label>Gender</Form.Label><Form.Control value={state.patient.gender} onChange={(e) => updatePatient('gender', e.target.value)} /></Form.Group></Col>
              <Col md={2}><Form.Group><Form.Label>Weight</Form.Label><Form.Control value={state.patient.weight} onChange={(e) => updatePatient('weight', e.target.value)} /></Form.Group></Col>
              <Col md={2}><Form.Group><Form.Label>Other (e.g. CrCl)</Form.Label><Form.Control value={state.patient.otherDetails} onChange={(e) => updatePatient('otherDetails', e.target.value)} placeholder="e.g. CrCl 30" /></Form.Group></Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Clinical context</Form.Label>
                  <Form.Control as="textarea" rows={3} value={state.context} onChange={(e) => setState((s) => ({ ...s, context: e.target.value }))} placeholder="e.g. Day 1 admission. PMH: CKD3, T2DM. HAP started today." />
                </Form.Group>
              </Col>
            </Row>
            <hr />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Allergies</h6>
              <Button size="sm" variant="outline-secondary" onClick={addAllergy}>+ Add allergy</Button>
            </div>
            {state.allergies.map((a, i) => (
              <Row key={i} className="g-2 mb-2 align-items-end">
                <Col md={4}><Form.Control placeholder="Drug / class" value={a.drug} onChange={(e) => updateAllergy(i, 'drug', e.target.value)} /></Col>
                <Col md={7}><Form.Control placeholder="Reaction" value={a.reaction} onChange={(e) => updateAllergy(i, 'reaction', e.target.value)} /></Col>
                <Col md={1}>
                  {state.allergies.length > 1 ? (
                    <Button size="sm" variant="outline-danger" onClick={() => removeAllergy(i)}>×</Button>
                  ) : null}
                </Col>
              </Row>
            ))}
          </div>
        </div>

        <div ref={chartRef} className={`card mb-3 ${tutorial.isStepActive('chart') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Prescription chart</h5>
              <Button size="sm" variant="outline-secondary" onClick={addRx}>+ Add prescription</Button>
            </div>
            <p className="text-muted small mb-3">Add the prescriptions that the student will see on the chart. Make at least one of them deliberately wrong - the wrong ones go in the "Expected issues" section below.</p>
            {state.prescriptionList.map((rx, i) => (
              <Row key={i} className="g-2 mb-2 align-items-end">
                <Col md={3}><Form.Control placeholder="Drug" value={rx.drug} onChange={(e) => updateRx(i, 'drug', e.target.value)} /></Col>
                <Col md={2}><Form.Control placeholder="Dose" value={rx.dose} onChange={(e) => updateRx(i, 'dose', e.target.value)} /></Col>
                <Col md={2}><Form.Control placeholder="Frequency" value={rx.frequency} onChange={(e) => updateRx(i, 'frequency', e.target.value)} /></Col>
                <Col md={2}><Form.Control placeholder="Route" value={rx.route} onChange={(e) => updateRx(i, 'route', e.target.value)} /></Col>
                <Col md={2}><Form.Control placeholder="Indication" value={rx.indication} onChange={(e) => updateRx(i, 'indication', e.target.value)} /></Col>
                <Col md={1}>
                  {state.prescriptionList.length > 1 ? (
                    <Button size="sm" variant="outline-danger" onClick={() => removeRx(i)}>×</Button>
                  ) : null}
                </Col>
              </Row>
            ))}
          </div>
        </div>

        <div ref={issuesRef} className={`card mb-3 ${tutorial.isStepActive('issues') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Expected issues (the answer key)</h5>
              <Button size="sm" variant="outline-secondary" onClick={addIssue}>+ Add issue</Button>
            </div>
            <p className="text-muted small mb-3">For each issue, set the drug name (must match a prescription above), pick the issue type, and add a short explanation that the student will see in the post-submit feedback.</p>
            {state.expectedIssues.map((iss, i) => (
              <Row key={i} className="g-2 mb-3 p-2 border rounded">
                <Col md={3}><Form.Group><Form.Label className="small mb-1">Drug (must match chart)</Form.Label><Form.Control value={iss.drug} onChange={(e) => updateIssue(i, 'drug', e.target.value)} /></Form.Group></Col>
                <Col md={3}><Form.Group><Form.Label className="small mb-1">Issue type</Form.Label>
                  <Form.Select value={iss.issueType} onChange={(e) => updateIssue(i, 'issueType', e.target.value)}>
                    {ISSUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Form.Select>
                </Form.Group></Col>
                <Col md={5}><Form.Group><Form.Label className="small mb-1">Explanation</Form.Label><Form.Control as="textarea" rows={2} value={iss.explanation} onChange={(e) => updateIssue(i, 'explanation', e.target.value)} placeholder="e.g. CrCl 30 - reduce metformin dose; lactic-acidosis risk." /></Form.Group></Col>
                <Col md={1} className="d-flex align-items-end">
                  {state.expectedIssues.length > 1 ? (
                    <Button size="sm" variant="outline-danger" onClick={() => removeIssue(i)}>×</Button>
                  ) : null}
                </Col>
              </Row>
            ))}
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mb-4">
          <Button type="button" variant="outline-secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={saving || validation.length > 0}
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create draft'}
          </Button>
        </div>
      </Container>
      <GuidedTutorialOverlay tutorial={tutorial} titleId="spotErrorEditorTutorialTitle" />
    </div>
  );
};

export default FacilitatorSpotErrorEditor;
